import { useEffect, useState } from 'react';
import {
  getDirectAccessToken,
  hasRefreshSessionHint,
} from '@/helpers/authSession';
import { clearLocalSession } from '@/store/services/userServices';
import { refreshAccessToken } from '@/store/tokenRefresh';
import { useUserStore } from '@/store/userStore';

/**
 * Performs a silent token refresh on mount by calling /api/token/refresh/
 * with the httpOnly refresh cookie. Blocks app rendering until the check
 * resolves so routes never flash as unauthenticated on a hard reload.
 *
 * @returns {{ isAuthReady: boolean }} — false until the refresh attempt settles
 */

const useSilentRefresh = () => {
  const [isAuthReady, setIsAuthReady] = useState(
    () => !getDirectAccessToken() && !hasRefreshSessionHint(),
  );

  useEffect(() => {
    // Impersonation is keyed off the token itself, which lives in
    // sessionStorage: the refresh hint is shared across the whole browser
    // and must never decide what this particular tab is.
    const directToken = getDirectAccessToken();
    if (directToken) {
      useUserStore.getState().setAccessToken(directToken);
      setIsAuthReady(true);
      return;
    }

    if (!hasRefreshSessionHint()) {
      useUserStore.getState().clearTokens();
      setIsAuthReady(true);
      return;
    }

    refreshAccessToken()
      .catch(() => {
        clearLocalSession({ redirect: false });
      })
      .finally(() => {
        setIsAuthReady(true);
      });
  }, []);

  return { isAuthReady };
};

export default useSilentRefresh;
